import React from 'react';
import { useTranslation } from 'react-i18next';
import { getIndustryColor } from '../lib/industryColor';

export default function InlineIndustryInput({ value, options, onSave }) {
  const { t } = useTranslation('client');
  const [editing, setEditing] = React.useState(false);
  const [inputVal, setInputVal] = React.useState(value || '');
  const [matches, setMatches] = React.useState([]);
  const [activeIdx, setActiveIdx] = React.useState(0);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editing]);

  const handleInputChange = (val) => {
    setInputVal(val);
    if (!val.trim()) {
      setMatches([]);
      setActiveIdx(0);
      return;
    }
    const filtered = options
      .filter(o => o.toLowerCase().includes(val.toLowerCase()) && o.toLowerCase() !== val.toLowerCase())
      .slice(0, 7);
    setMatches(filtered);
    setActiveIdx(0);
  };

  const handleKeyDown = async (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (matches.length > 0) {
        setActiveIdx(prev => (prev + 1) % matches.length);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (matches.length > 0) {
        setActiveIdx(prev => (prev - 1 + matches.length) % matches.length);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const finalVal = matches.length > 0 ? matches[activeIdx] : inputVal.trim();
      setEditing(false);
      setMatches([]);
      await onSave(finalVal);
    } else if (e.key === 'Escape') {
      setEditing(false);
      setMatches([]);
      setInputVal(value || '');
    }
  };

  const handleBlur = () => {
    // Delay slightly to let onClick fire on dropdown
    setTimeout(async () => {
      setEditing(false);
      setMatches([]);
      if (inputVal.trim() !== (value || '')) {
        await onSave(inputVal.trim());
      }
    }, 150);
  };

  const selectMatch = async (matchVal) => {
    setEditing(false);
    setMatches([]);
    await onSave(matchVal);
  };

  if (editing) {
    return (
      <div className="inline-select-wrap" onClick={e => e.stopPropagation()} style={{ position: 'relative', width: '100%' }}>
        <input
          ref={inputRef}
          type="text"
          aria-label={t('industryInput.ariaLabel')}
          value={inputVal}
          onChange={e => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          style={{
            width: '100%',
            background: '#0d0d0f',
            color: '#fff',
            border: '1px solid rgba(59, 130, 246, 0.6)',
            borderRadius: 'var(--radius-sm)',
            padding: '2px var(--space-2)',
            fontSize: '10px',
            outline: 'none'
          }}
        />
        {matches.length > 0 && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: '#16161a',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 'var(--radius-sm)',
            zIndex: 1000,
            boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
            maxHeight: '150px',
            overflowY: 'auto'
          }}>
            {matches.map((m, idx) => (
              <div
                key={m}
                onMouseDown={() => selectMatch(m)}
                style={{
                  padding: 'var(--space-1) var(--space-2)',
                  fontSize: '10px',
                  color: '#fff',
                  cursor: 'pointer',
                  background: idx === activeIdx ? 'rgba(59, 130, 246, 0.25)' : 'transparent',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {m}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  const colors = getIndustryColor(value);

  return (
    <div className="inline-select-wrap" onClick={e => { e.stopPropagation(); setInputVal(value || ''); setEditing(true); }}>
      <span className="client-badge" style={{
        backgroundColor: colors.bg,
        color: colors.fg,
        borderColor: colors.border,
        borderWidth: '1px',
        borderStyle: 'solid',
        padding: '2px var(--space-2)',
        borderRadius: 'var(--radius-sm)',
        fontSize: '10px',
        fontWeight: '600',
        cursor: 'pointer',
        display: 'inline-block',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '110px'
      }}>
        {value || '—'}
      </span>
    </div>
  );
}
